import { Avatar, Button, Divider, Drawer, Icon, Typography } from "@/components/atoms";
import { ClientProfile } from "@/redux/apis/clientProfile/types";
import { getAssetUrl } from "@/utils";

interface YourDetailsMobileProps {
  profile: ClientProfile;
}

const getInitials = (firstName: string, lastName: string) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

const ContactRow = ({ icon, label, value }: { icon: string; label: string; value: string | null | undefined }) => (
  <div className="flex items-center gap-3">
    <div className="flex shrink-0 items-center justify-center rounded bg-neutral-gray p-3">
      <Icon name={icon} size="sm" className="text-neutral-dark-gray" />
    </div>
    <div className="flex min-w-0 flex-col gap-1">
      <Typography variant="paragraph" className="text-neutral-silver-gray">
        {label}
      </Typography>
      <Typography variant="title" className="break-all">
        {value || "-"}
      </Typography>
    </div>
  </div>
);

const DetailValue = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <div className="flex items-baseline justify-between py-1">
    <Typography variant="paragraph" className="shrink-0 text-neutral-silver-gray">
      {label}
    </Typography>
    <Typography variant="title" className="text-right">
      {value || "-"}
    </Typography>
  </div>
);

export const YourDetailsMobile = ({ profile }: YourDetailsMobileProps) => {
  const { personal, company, preferences, avatar } = profile;
  const fullName = `${personal.first_name} ${personal.last_name}`.trim();
  const avatarUrl = avatar ? getAssetUrl(avatar, { height: 160, width: 160, fit: "cover" }) : undefined;

  return (
    <Drawer>
      {/* Trigger — Compact Card */}
      <button className="flex w-full items-center gap-4 rounded-lg border border-neutral-mid-gray bg-white p-5">
        <Avatar size="md" src={avatarUrl} alt={fullName} className="h-12 w-12 bg-neutral-mid-gray">
          {!avatar && (
            <Typography variant="action" className="text-neutral-dark-gray">
              {getInitials(personal.first_name, personal.last_name)}
            </Typography>
          )}
        </Avatar>
        <div className="flex flex-1 flex-col items-start">
          <Typography variant="paragraph" className="text-neutral-silver-gray">
            Your Details
          </Typography>
          <Typography variant="h3">{fullName}</Typography>
        </div>
        <Icon name="ArrowRight2" size="sm" className="text-neutral-dark-gray" />
      </button>

      {/* Content */}
      <div className="flex flex-col gap-5 px-4 pb-12 pt-4">
        <Typography variant="title">Your Details</Typography>

        {/* Avatar + Name */}
        <div className="flex flex-col items-start gap-2">
          <Avatar size="xl" src={avatarUrl} alt={fullName} className="h-[72px] w-[72px] bg-neutral-mid-gray">
            {!avatar && (
              <Typography variant="h3" className="text-neutral-dark-gray">
                {getInitials(personal.first_name, personal.last_name)}
              </Typography>
            )}
          </Avatar>
          <div>
            <Typography variant="paragraph" className="text-neutral-silver-gray">
              Passenger
            </Typography>
            <Typography variant="h2">{fullName}</Typography>
          </div>
        </div>

        {/* Contact Info */}
        <div className="flex flex-col gap-4">
          <ContactRow icon="Sms" label="Email address" value={personal.email} />
          <ContactRow icon="Call" label="Mobile no." value={personal.mobile} />
          <ContactRow icon="Building" label="Company" value={company.company_name} />
        </div>

        <Divider />

        {/* Preferences */}
        <div className="flex flex-col gap-1">
          <DetailValue label="Seating Preference" value={preferences.seating} />
          <DetailValue label="Radio Preference" value={preferences.radio} />
          <DetailValue label="Quiet Ride" value={preferences.quiet_ride} />
          <DetailValue label="Vehicle Temp" value={preferences.vehicle_temp ? `${preferences.vehicle_temp}° C` : undefined} />
          <DetailValue
            label="Airport Waiting Time Preference"
            value={
              preferences.airport_waiting_time
                ? `${preferences.airport_waiting_time} ${preferences.airport_waiting_timeframe === "minute" ? "min" : "hour"}${
                    Number(preferences.airport_waiting_time) !== 1 ? "s" : ""
                  }`
                : undefined
            }
          />

          <div className="flex flex-col py-1">
            <Typography variant="paragraph" className="text-neutral-silver-gray">
              Notes
            </Typography>
            <Typography variant="title">
              {preferences.driver_notes || "-"}
            </Typography>
          </div>
        </div>

        {/* Close Button */}
        <Drawer.Close asChild>
          <Button variant="tertiary" size="lg" className="w-full">
            Close
          </Button>
        </Drawer.Close>
      </div>
    </Drawer>
  );
};
